// src/App.js
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { useAuth } from "./context/AuthContext";

// ✅ TopBar (now disabled)
import TopBar from "./components/TopBar";

// ✅ Admin layout wrapper for left icon menu
import AdminLayout from "./components/AdminLayout";

// ✅ Staff layout wrapper for left icon menu
import StaffLayout from "./components/StaffLayout";

// Pages
import LoginPage from "./pages/LoginPage";
import MySchedulePage from "./pages/MySchedulePage";
import TeamSchedulePage from "./pages/TeamSchedulePage";
import AdminDashboardPage from "./pages/AdminDashboardPage";
import AdminScheduleEditorPage from "./pages/AdminScheduleEditorPage";
import AdminStaffPage from "./pages/AdminStaffPage";
import AdminDepartmentsPage from "./pages/AdminDepartmentsPage";
import AdminCalendarPage from "./pages/AdminCalendarPage";
import AdminAttendanceTodayPage from "./pages/AdminAttendanceTodayPage";

// ✅ User Calendar (read-only)
import UserCalendarPage from "./pages/UserCalendarPage";

// Protected Route
import ProtectedRoute from "./components/ProtectedRoute";

import ChangePasswordPage from "./pages/ChangePasswordPage";


function LoginRedirect({ getDefaultRoute }) {
  const { user } = useAuth();
  const location = useLocation();

  const fromRaw = location.state?.from;
  const fromPath = typeof fromRaw === "string" ? fromRaw : fromRaw?.pathname;

  if (user) {
    return <Navigate to={fromPath || getDefaultRoute()} replace />;
  }

  return <LoginPage />;
}

function App() {
  const { user } = useAuth();

  const getDefaultRoute = () => {
    if (!user) return "/login";

    const role = String(user?.role || "").toLowerCase();
    const isAdmin =
      role === "superadmin" ||
      role === "manager" ||
      role === "ops_supervisor" ||
      role === "lead";

    return isAdmin ? "/admin/dashboard" : "/my-schedule";
  };


  return (
    <Router>
      <TopBar />

      <div>
        <Routes>
          <Route path="/" element={<Navigate to={getDefaultRoute()} replace />} />
          <Route path="/login" element={<LoginRedirect getDefaultRoute={getDefaultRoute} />} />

          {/* ===================== USER ROUTES ===================== */}
          <Route
            path="/my-schedule"
            element={
              <ProtectedRoute role="user">
                {user && ["superadmin","manager","ops_supervisor","lead"].includes(user.role)
                  ? <Navigate to="/admin/dashboard" replace />
                  : <MySchedulePage />
                }
              </ProtectedRoute>
            }
          />

          <Route
              path="/change-password"
              element={
                <ProtectedRoute>
                  <ChangePasswordPage />
                </ProtectedRoute>
              }
            />

          {/* ✅ staff team schedule route (WRAPPED WITH StaffLayout) */}
          <Route
            path="/user/team-schedule"
            element={
              <ProtectedRoute role="user">
                <StaffLayout>
                  <TeamSchedulePage />
                </StaffLayout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/user/calendar"
            element={
              <ProtectedRoute role="user">
                <UserCalendarPage />
              </ProtectedRoute>
            }
          />

          {/* ===================== ADMIN ROUTES ===================== */}
          <Route
            path="/team-schedule"
            element={
              <ProtectedRoute role="admin">
                <AdminLayout>
                  <TeamSchedulePage />
                </AdminLayout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/admin/dashboard"
            element={
              <ProtectedRoute role="admin">
                <AdminLayout>
                  <AdminDashboardPage />
                </AdminLayout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/admin/schedule"
            element={
              <ProtectedRoute role="admin">
                <AdminLayout>
                  <AdminScheduleEditorPage />
                </AdminLayout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/admin/calendar"
            element={
              <ProtectedRoute role="admin">
                <AdminLayout>
                  <AdminCalendarPage />
                </AdminLayout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/admin/staff"
            element={
              <ProtectedRoute role="admin">
                <AdminLayout>
                  <AdminStaffPage />
                </AdminLayout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/admin/departments"
            element={
              <ProtectedRoute role="admin">
                <AdminLayout>
                  <AdminDepartmentsPage />
                </AdminLayout>
              </ProtectedRoute>
            }
          />

          <Route
              path="/admin/attendance-today"
              element={
                <ProtectedRoute role="admin">
                  <AdminLayout>
                    <AdminAttendanceTodayPage />
                  </AdminLayout>
                </ProtectedRoute>
              }
            />
            
          <Route path="*" element={<Navigate to={getDefaultRoute()} replace />} />
        </Routes>
      </div>
    </Router>
  );
}

export default App;
